import { axiosInstance } from "contexts/axiosApiInstance";

/// A free-floating, user-owned query result (created by the MCP
/// `create_table_from_sql` tool). Mirrors the server `ComputedTableDetails`.
/// Computed tables are NOT shareable — only the owner sees them.
export interface ComputedTable {
  computed_table_id: string;
  name: string;
  /// Author-provided description (from `create_table_from_sql`). May be empty
  /// for tables created before descriptions were required.
  description: string;
  /// Unix seconds — when the query result was materialized.
  created_at: number;
  row_count: number;
  /// True when a source table the query reads has advanced past the version
  /// this computed table was materialized against.
  stale: boolean;
}

/// List the computed tables the current user owns, newest first. Powers the
/// overview "Computed tables" section.
export const listComputedTables = async (): Promise<ComputedTable[]> => {
  const { data } = await axiosInstance.get<ComputedTable[]>(
    "/action/computed_tables",
  );
  return data;
};

/// Absolute URL to download a computed table as CSV (default) or Parquet.
/// Points at data-service (same origin as the app, so the browser session
/// cookie authenticates it). Opened directly by the browser so the attachment
/// downloads without buffering through JS.
export const computedTableDownloadUrl = (
  computedTableId: string,
  format: "csv" | "parquet" = "csv",
): string => {
  const base = (import.meta.env.VITE_APP_DATA_SERVICE ?? "").replace(
    /\/+$/,
    "",
  );
  const query = format === "parquet" ? "?format=parquet" : "";
  return `${base}/tables/${computedTableId}/download${query}`;
};

/// Import a computed table into a model as a read-only referenced table
/// (no data copy). Requires edit access to the target model.
export const importComputedTable = async (
  computedTableId: string,
  targetModelId: string,
): Promise<{ table_id: string; model_id: string }> => {
  const { data } = await axiosInstance.post("/action/import_computed_table", {
    computed_table_id: computedTableId,
    target_model_id: targetModelId,
  });
  return data;
};

/// Refresh a computed table: re-run its stored query against current source
/// data and overwrite the result in place. Resolves on success; rejects (with
/// the server's reason) when a source column was casted or the query no longer
/// returns data.
export const refreshComputedTable = async (
  computedTableId: string,
): Promise<void> => {
  await axiosInstance.post("/action/refresh_computed_table", {
    computed_table_id: computedTableId,
  });
};

/// Permanently delete a computed table.
export const deleteComputedTable = async (
  computedTableId: string,
): Promise<void> => {
  await axiosInstance.post("/action/delete_computed_table", {
    computed_table_id: computedTableId,
  });
};

/// What a source version IS right now (reverse lookup, not recorded at build).
/// Mirrors the server `VersionLabel` (`#[serde(tag = "kind", ...)]`).
export type VersionLabel =
  | { kind: "released"; name: string }
  | { kind: "checkpoint"; name: string }
  | { kind: "unnamed" }
  /// The built-from version was pruned by compaction — its cut is gone.
  | { kind: "not_retained" };

/// Why a source row can't show provenance. Mirrors server `SourceStatus`.
export type SourceStatus = "ok" | "trashed" | "access_removed";

/// One source table a computed table was built from. `built_label` /
/// `is_behind` / `current_label` are only meaningful when `status === "ok"`.
export interface ComputedTableSource {
  source_table_id: string;
  source_name: string | null;
  status: SourceStatus;
  built_label: VersionLabel;
  /// True when the source has advanced past the version this table was built
  /// on. Deliberately a boolean, not a count (version numbers also bump on
  /// schema-only edits, so a count would mislead).
  is_behind: boolean;
  /// What the source's current serving version is now — only when `is_behind`.
  current_label: VersionLabel | null;
  /// The source's parent model id + name, when the user can view that model
  /// (so the name can link to it). Both `null` on a viewable source
  /// (`status === "ok"`) means the table is shared directly — you have the
  /// table but not its parent model.
  parent_model_id: string | null;
  parent_model_name: string | null;
}

/// The source tables a computed table was built from, with each source's
/// build-time cut (resolved as it stands now) and whether it has moved on.
/// Powers the per-row "source versions" overlay. Owner/importer only.
export const getComputedTableSources = async (
  computedTableId: string,
): Promise<ComputedTableSource[]> => {
  const { data } = await axiosInstance.get<ComputedTableSource[]>(
    `/action/computed_table_sources/${computedTableId}`,
  );
  return data;
};
